import { useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Library as LibraryIcon, PanelLeftClose, PanelLeftOpen, Plus, Search, Settings, Users, Activity, Trash2, Archive, Copy } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useLibraries } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useUiStore } from '@/stores/ui';
import { cx } from '@/lib/format';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '@/config/constants';
import { IconButton } from '@/components/Button';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { BoardTree } from './BoardTree';
import { CreateBoardDialog } from '@/features/boards/BoardDialogs';
import { usePermission } from './usePermission';
// --- release1/B ---
import { PinnedSection } from '@/features/pins/PinnedSection';
import { useTidyPrefs } from '@/stores/gridPrefs';
// --- release2/A ---
import { SystemBoardsSection } from '@/features/smart-boards/SystemBoardsSection';
// --- release3/D ---
import { useGuestMode } from '@/features/board-guests/useGuestMode';

export function Sidebar() {
  const { ws = '' } = useParams();
  const wsId = useSessionStore((s) => s.workspace?.id);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const drawer = useUiStore((s) => s.sidebarDrawerOpen);
  const setDrawer = useUiStore((s) => s.setSidebarDrawer);
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { active: drawer, onEscape: () => setDrawer(false) });
  const libraries = useLibraries(wsId);
  const [create, setCreate] = useState<{ parent: Board | null; libraryId: string } | null>(null);
  const canMembers = usePermission('MANAGE_MEMBERS');
  const canAdmin = usePermission('ADMIN');
  const canShare = usePermission('SHARE');
  const canSettings = canMembers || canAdmin || canShare;
  const canTrash = usePermission('MANAGE_BOARD'); // release1/B
  const showArchived = useTidyPrefs((s) => s.showArchived);
  const setShowArchived = useTidyPrefs((s) => s.setShowArchived);
  const guest = useGuestMode(); // release3/D: a guest sees only granted subtrees; workspace-wide pages stay hidden

  const nav = (to: string, icon: React.ReactNode, label: string) => (
    <NavLink to={to} onClick={() => setDrawer(false)} className={({ isActive }) => cx('flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-bg-sunken', isActive && 'bg-bg-sunken font-medium text-accent', collapsed && 'justify-center px-0')} title={label}>
      {icon}
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );

  const body = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 p-2">
        <div className="min-w-0 flex-1">
          <WorkspaceSwitcher collapsed={collapsed} />
        </div>
        {!collapsed && <IconButton label="Collapse sidebar" onClick={toggle} className="hidden md:inline-flex"><PanelLeftClose size={16} /></IconButton>}
      </div>
      {collapsed && (
        <div className="flex justify-center pb-1">
          <IconButton label="Expand sidebar" onClick={toggle}><PanelLeftOpen size={16} /></IconButton>
        </div>
      )}
      <nav className="space-y-px px-2" aria-label="Workspace">
        {nav(`/w/${ws}/search`, <Search size={16} />, 'Search')}
        {!guest && nav(`/w/${ws}/people`, <Users size={16} />, 'People')} {/* release3/D */}
      </nav>
      <PinnedSection collapsed={collapsed} onNavigate={() => setDrawer(false)} /> {/* release1/B */}
      <SystemBoardsSection collapsed={collapsed} onNavigate={() => setDrawer(false)} /> {/* release2/A */}
      <div className="scrollbar-thin mt-2 min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {!collapsed && (
          <label className="mt-2 flex cursor-pointer items-center gap-1.5 px-2 text-xs text-fg-subtle select-none" title="Archived boards and assets stay hidden unless this is on">
            <input type="checkbox" className="h-3 w-3" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" />
            <Archive size={12} /> Show archived
          </label>
        )}
        {libraries.data?.map((lib) => (
          <section key={lib.id} className="mt-2">
            <div className={cx('group flex items-center gap-1 px-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase', collapsed && 'justify-center')}>
              <NavLink to={`/w/${ws}/l/${lib.id}`} onClick={() => setDrawer(false)} className={({ isActive }) => cx('flex items-center gap-1 rounded px-1 py-1 hover:text-fg', isActive && 'text-accent')} title={lib.name}>
                <LibraryIcon size={13} />
                {!collapsed && <span className="truncate">{lib.name}</span>}
              </NavLink>
              {!collapsed && !guest && ( /* release3/D */
                <button type="button" aria-label={`New board in ${lib.name}`} className="invisible ml-auto rounded p-0.5 group-hover:visible hover:bg-bg-sunken" onClick={() => setCreate({ parent: null, libraryId: lib.id })}>
                  <Plus size={13} />
                </button>
              )}
            </div>
            {!collapsed && <BoardTree libraryId={lib.id} onCreate={(parent, libraryId) => setCreate({ parent, libraryId })} />}
          </section>
        ))}
      </div>
      <div className="border-t border-line p-2">
        {import.meta.env.DEV && nav(`/w/${ws}/dev/perf`, <Activity size={16} />, 'Perf (dev)')}
        {!guest && nav(`/w/${ws}/duplicates`, <Copy size={16} />, 'Duplicates')} {/* release1 integration: C's page, next to B's Trash; hidden for guests (release3/D) */}
        {canTrash && nav(`/w/${ws}/trash`, <Trash2 size={16} />, 'Trash')}
        {canSettings && nav(`/w/${ws}/settings`, <Settings size={16} />, 'Settings')}
      </div>
      {create && <CreateBoardDialog parent={create.parent} libraryId={create.libraryId} onClose={() => setCreate(null)} />}
    </div>
  );

  return (
    <>
      <aside className="hidden h-full shrink-0 border-r border-line bg-bg-elev md:block" style={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH }} aria-label="Sidebar">
        {body}
      </aside>
      {drawer && (
        <div ref={drawerRef} className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(false)} />
          <aside className="absolute inset-y-0 left-0 w-[272px] bg-bg-elev shadow-xl">{body}</aside>
        </div>
      )}
    </>
  );
}
